import { singleton, onCleanup } from './cleanup.js'

/* Estado compartido entre la instalación singleton y cada revinculación */
let deviceRef = null
let footerVisible = false
let footerObserved = null
let lastScrollTop = 0

/**
 * Aplica el marcador de scroll sobre el body.
 * Antes esto se recalculaba creando un IntersectionObserver nuevo dentro del
 * handler de scroll (cientos de observers vivos por cada scroll del usuario).
 * Ahora el observer es único y sólo actualiza la bandera `footerVisible`.
 */
function applyScrollState () {
  const body = document.body

  if (footerVisible) {
    body.setAttribute('data-scroll', 'bottom')
  } else if (window.scrollY < 100) {
    body.setAttribute('data-scroll', 'top')
  } else {
    body.setAttribute('data-scroll', 'down')
  }
}

/**
 * Muestra/oculta el menú según la dirección del scroll.
 */
function smartMenu () {
  const st = window.scrollY

  document.body.dataset.stack = st > lastScrollTop ? 'off' : 'on'

  lastScrollTop = st
}

/**
 * ¿Corresponde aplicar el comportamiento de menú inteligente en este device?
 * @returns {boolean}
 */
function smartMenuEnabled () {
  if (!deviceRef) return false

  const { platform, isMobile, isDesktop, isTablet } = deviceRef

  if (platform !== 'ios' && isMobile === true) return true
  return isDesktop === true || isTablet === true
}

/**
 * Observer único sobre el footer. Se crea una sola vez; en cada transición
 * simplemente se le reasigna el footer nuevo.
 * @returns {IntersectionObserver}
 */
function createFooterObserver () {
  return new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      footerVisible = entry.isIntersecting
    })
    applyScrollState()
  }, {
    root: null, // Usar el viewport como área de observación
    rootMargin: '0px', // Margen adicional alrededor del área de observación
    threshold: 0.5 // Porcentaje del elemento que debe estar visible para activar la función de callback
  })
}

/**
 * Handler de scroll único, limitado a un cálculo por frame.
 * @returns {(e: Event) => void}
 */
function createScrollHandler () {
  let ticking = false

  const onScroll = () => {
    if (ticking) return
    ticking = true

    requestAnimationFrame(() => {
      ticking = false
      applyScrollState()
      if (smartMenuEnabled()) smartMenu()
    })
  }

  window.addEventListener('scroll', onScroll, { passive: true })

  return onScroll
}

/**
 * @param {object} device_data Datos de dispositivo vigentes
 */
function scrollMarkers (device_data) {
  deviceRef = device_data

  // Instalados una única vez: viven en window y sobreviven a las transiciones
  const observer = singleton('scrollMarkers:observer', createFooterObserver)
  singleton('scrollMarkers:scroll', createScrollHandler)

  // El <footer> vive dentro del transition-wrapper: en cada navegación es un
  // nodo NUEVO. Hay que soltar el anterior (que quedó detached) y observar
  // el actual, o el marcador 'bottom' deja de dispararse.
  const footer = document.querySelector('footer')

  if (footerObserved && footerObserved !== footer) {
    observer.unobserve(footerObserved)
    footerObserved = null
  }

  if (footer && footer !== footerObserved) {
    observer.observe(footer)
    footerObserved = footer

    onCleanup(() => {
      observer.unobserve(footer)
      if (footerObserved === footer) footerObserved = null
    })
  }

  footerVisible = false
  lastScrollTop = window.scrollY
  applyScrollState()
}

export default scrollMarkers
